import axios from "axios";
import { z } from "zod";

import { env } from "@/env";
import { useSessionStore } from "@/stores/use-session-store";
import { errorResponseInterceptor } from "./interceptors";

export const baseConfig = {
  baseURL: `${env.VITE_API_URL}/webapp`,
  headers: {
    "Content-Type": "application/json",
  },
};

export const postPurchaseBaseConfig = {
  baseURL: env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
};

export const postPurchaseApi = axios.create(postPurchaseBaseConfig);
export const publicApi = axios.create(baseConfig);
export const privateApi = axios.create(baseConfig);

privateApi.interceptors.request.use((config) => {
  const userToken = useSessionStore.getState().session?.token;
  config.headers.Authorization = `Bearer ${userToken}`;

  return config;
});

privateApi.interceptors.response.use((response) => {
  return response;
}, errorResponseInterceptor);

const paginationSchema = z.object({
  count: z.number(),
  total: z.number(),
  perPage: z.number(),
  currentPage: z.number(),
  totalPages: z.number(),
  links: z.object({
    previous: z.string().optional(),
    next: z.string().optional(),
  }),
});

export const createServiceResponseSchema = <T extends z.ZodTypeAny, U extends z.ZodTypeAny>(
  dataSchema: T,
  metaSchema?: U,
) => {
  return z.object({
    status: z.number(),
    success: z.boolean(),
    data: dataSchema,
    meta: metaSchema ? metaSchema.optional() : z.undefined().optional(),
    pagination: paginationSchema.optional(),
  });
};

export type ServiceResponse<T = unknown> = {
  status: number;
  success: boolean;
  data: T;
  pagination?: {
    count: number;
    total: number;
    perPage: number;
    currentPage: number;
    totalPages: number;
    links: {
      previous: string;
      next: string;
    };
  };
};
